import { useEffect, useCallback, useRef } from 'react';
import { eventBus, StockEvents } from '@/lib/eventBus';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { enqueueEffect, getSyncQueue, removeEffect } from '@/lib/syncQueue';
import { formatLocalDate } from '@/lib/dateUtils';

export function StockEventHandler() {
  const isProcessingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || !navigator.onLine) return;

    const queue = getSyncQueue();
    // Filter for stock events
    const stockEvents = queue.filter(e => 
      e.type.startsWith('STOCK_') || 
      (e.payload && (e.payload.type === 'package-create' || e.payload.type === 'package-update' || e.payload.type === 'package-delete'))
    );

    if (stockEvents.length === 0) return;

    isProcessingRef.current = true;
    console.log(`StockEventHandler: Processing ${stockEvents.length} pending actions`);

    for (const effect of stockEvents) {
      try {
        let success = false;
        
        if (effect.type === StockEvents.CREATE) {
          const { item, teamId } = effect.payload;
          const { error } = await supabase.from('stock_items').insert({
            team_id: teamId,
            name: item.name,
            description: item.description,
            unit: item.unit,
            current_quantity: item.currentQuantity,
            package_size: item.packageSize,
            reorder_threshold: item.reorderThreshold,
            refill_threshold: item.refillThreshold,
            category: item.category,
            category_id: item.categoryId,
            expiry_date: item.expiryDate ? formatLocalDate(new Date(item.expiryDate)) : null,
          });
          if (!error) success = true;
        } 
        else if (effect.type === StockEvents.UPDATE) {
          // Handle both generic updates and package specific ones
          if (effect.payload.type === 'package-create') {
            const { stockItemId, quantity, count, teamId, status, purchaseDate, expiryDate } = effect.payload.payload;
            const packagesToInsert = Array.from({ length: count }, () => ({
                stock_item_id: stockItemId,
                team_id: teamId,
                quantity,
                original_quantity: quantity,
                status,
                purchase_date: purchaseDate ? formatLocalDate(new Date(purchaseDate)) : null,
                expiry_date: expiryDate ? formatLocalDate(new Date(expiryDate)) : null,
            }));
            const { error } = await supabase.from('stock_packages').insert(packagesToInsert);
            if (!error) success = true;
          } else if (effect.payload.type === 'package-update') {
            const { id, updates } = effect.payload.payload;
            const dbUpdates: any = {};
            if (updates.quantity !== undefined) dbUpdates.quantity = updates.quantity;
            if (updates.status !== undefined) dbUpdates.status = updates.status;
            const { error } = await supabase.from('stock_packages').update(dbUpdates).eq('id', id);
            if (!error) success = true;
          } else {
            const { id, updates } = effect.payload;
            const dbUpdates: any = {};
            if (updates.currentQuantity !== undefined) dbUpdates.current_quantity = updates.currentQuantity;
            const { error } = await supabase.from('stock_items').update(dbUpdates).eq('id', id);
            if (!error) success = true;
          }
        }
        else if (effect.type === StockEvents.RECORD_USAGE || effect.type === StockEvents.ADD_STOCK) {
          const { itemId, teamId, quantity, memberId, deviceId, notes, type } = effect.payload;
          
          // 1. Insert transaction
          const { error: txError } = await supabase
            .from('stock_transactions')
            .insert({
              stock_item_id: itemId,
              team_id: teamId,
              quantity_change: effect.type === StockEvents.RECORD_USAGE ? -quantity : quantity,
              transaction_type: effect.type === StockEvents.RECORD_USAGE ? 'use' : 'purchase',
              notes,
              created_by_member_id: memberId,
              created_by_device_id: deviceId,
            });

          // 2. Update stock level
          const { error: updateError } = await supabase
            .from('stock_items')
            .update({ current_quantity: effect.payload.newQuantity })
            .eq('id', itemId);

          if (!txError && !updateError) success = true;
        }
        else if (effect.type === StockEvents.DELETE) {
           if (effect.payload.type === 'package-delete') {
             const { id } = effect.payload.payload;
             const { error } = await supabase.from('stock_packages').delete().eq('id', id);
             if (!error) success = true;
           } else {
             const { id } = effect.payload;
             const { error } = await supabase.from('stock_items').delete().eq('id', id);
             if (!error) success = true;
           }
        }

        if (success) {
          removeEffect(effect.id);
        } else {
          // Break to retry later if a specific call fails (could be transient)
          break;
        }
      } catch (err) {
        console.error('Failed to process stock sync effect:', effect, err);
        break;
      }
    }

    isProcessingRef.current = false;
  }, []);

  useEffect(() => {
    const handleCreate = (payload: any) => {
      const isOffline = !navigator.onLine;
      if (isOffline) {
        enqueueEffect(StockEvents.CREATE, payload);
        toast.info(`Offline: ${payload.item.name} in wachtrij`, { channel: 'stock' });
      } else {
        // Immediate sync if online
        enqueueEffect(StockEvents.CREATE, payload);
        processQueue();
        toast.success(`${payload.item.name} toegevoegd`, { channel: 'stock' });
      }
    };

    const handleUpdate = (payload: any) => {
      enqueueEffect(StockEvents.UPDATE, payload);
      if (navigator.onLine) processQueue();
    };

    const handleDelete = (payload: any) => {
      enqueueEffect(StockEvents.DELETE, payload);
      if (navigator.onLine) processQueue();
      if (payload.name) toast.success(`${payload.name} verwijderd`, { channel: 'stock' });
    };

    const handleUsage = (payload: any) => {
      enqueueEffect(StockEvents.RECORD_USAGE, payload);
      if (navigator.onLine) {
        processQueue();
        toast.success(`${payload.itemName}: -${payload.quantity} ${payload.unit}`, { channel: 'stock' });
      } else {
        toast.info(`Offline: -${payload.quantity} ${payload.unit} in wachtrij`, { channel: 'stock' });
      }
    };

    const handleAddStock = (payload: any) => {
      enqueueEffect(StockEvents.ADD_STOCK, payload);
      if (navigator.onLine) {
        processQueue();
        toast.success(`${payload.itemName}: +${payload.quantity} ${payload.unit}`, { channel: 'stock' });
      } else {
        toast.info(`Offline: +${payload.quantity} ${payload.unit} in wachtrij`, { channel: 'stock' });
      }
    };

    eventBus.on(StockEvents.CREATE, handleCreate);
    eventBus.on(StockEvents.UPDATE, handleUpdate);
    eventBus.on(StockEvents.DELETE, handleDelete);
    eventBus.on(StockEvents.RECORD_USAGE, handleUsage);
    eventBus.on(StockEvents.ADD_STOCK, handleAddStock);

    window.addEventListener('online', processQueue);
    processQueue();

    return () => {
      eventBus.off(StockEvents.CREATE, handleCreate);
      eventBus.off(StockEvents.UPDATE, handleUpdate);
      eventBus.off(StockEvents.DELETE, handleDelete);
      eventBus.off(StockEvents.RECORD_USAGE, handleUsage);
      eventBus.off(StockEvents.ADD_STOCK, handleAddStock);
      window.removeEventListener('online', processQueue);
    };
  }, [processQueue]);

  return null;
}
